import React from "react"
import { Tab, Tabs } from "react-bootstrap"
import { FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"

import { IconSize } from "shared/types/Icon"
import { FormProviderProps } from "shared/types/ReactHookForm"
import { SelectOption } from "shared/types/Select"
import { Button, ButtonVariant } from "shared/ui/buttons/Button"
import { Hint } from "shared/ui/hint/Hint"
import { IconName } from "shared/ui/icon/__generated__/IconName"
import { TextInput } from "shared/ui/input/text/TextInput"
import { Textarea } from "shared/ui/input/text/Textarea"
import { Link } from "shared/ui/link/Link"
import { OverlayHeader } from "shared/ui/overlay/OverlayHeader"
import { OverlayWrapper } from "shared/ui/overlay/OverlayWrapper"
import { Switch } from "shared/ui/switch/Switch"

import { ShareLinkInviteEmailFormData } from "features/project-sharing/model/useSendShareLinkInvite"

import { ShareProjectOverlayTab } from "./ShareProjectOverlay"

export interface ShareProjectOverlayViewModel {
  emailForm: FormProviderProps<ShareLinkInviteEmailFormData>
  activeKey: ShareProjectOverlayTab
  onActiveKeyChange: (nextActiveKey: ShareProjectOverlayTab | null) => void
  options: SelectOption<ShareProjectOverlayTab>[] | undefined
  shareLink?: string | null
  onCopy?: () => void
  onClose: () => void
  onSubmit: () => void
}

export const ShareProjectOverlayView = React.memo(
  ({
    emailForm,
    shareLink,
    activeKey,
    options,
    onActiveKeyChange,
    onCopy,
    onClose,
    onSubmit
  }: ShareProjectOverlayViewModel) => {
    const { t } = useTranslation()

    return (
      <OverlayWrapper
        id="share-project-overlay"
        form="share-project-overlay-form"
        className="share-project-overlay"
        classNamePrefix="share-project-overlay"
        headline={t("Project.Actions.Share")}
        mobileFullscreen
      >
        <OverlayHeader.Container prefix="share-project-overlay">
          <OverlayHeader.CloseButton
            onClick={onClose}
            className="primary"
            label={t("General.Close")}
            data-testid="share-project-overlay__cancel"
          />
          <OverlayHeader.Title>
            {t("Project.Actions.Share")}
          </OverlayHeader.Title>
        </OverlayHeader.Container>
        <div className="share-project-overlay__content">
          <Switch
            value={activeKey}
            options={options}
            onChange={onActiveKeyChange}
          />
          <Tabs hidden activeKey={activeKey}>
            <Tab
              title={t("Project.ShareViaLink")}
              key="share-via-link"
              eventKey={ShareProjectOverlayTab.LINK}
            >
              <div className="share-project-overlay__link-tab">
                <Hint.Root variant="warning" className="hint--banner">
                  <Hint.Icon name={IconName.WARNING} size={IconSize.SMALL} />
                  <Hint.Content>
                    <Hint.Text>
                      {t("Project.SharedProjectDisclaimer")}
                    </Hint.Text>
                  </Hint.Content>
                </Hint.Root>
                <TextInput.Root
                  loading={!shareLink}
                  label={t("Project.ShareLink")}
                >
                  <TextInput.Label />
                  <TextInput.Focus>
                    <TextInput.Input
                      value={shareLink ?? undefined}
                      spellCheck={false}
                      data-testid="share-project-overlay__link__input"
                    />
                    <TextInput.Affix>
                      <Link
                        data-testid="share-project-overlay__copy-button"
                        onClick={onCopy}
                      >
                        {t("General.CopyItem", { item: t("Link.Link") })}
                      </Link>
                    </TextInput.Affix>
                  </TextInput.Focus>
                </TextInput.Root>
              </div>
            </Tab>
            <Tab
              title={t("Project.ShareViaEmail")}
              key="share-via-email"
              eventKey={ShareProjectOverlayTab.EMAIL}
            >
              <FormProvider {...emailForm}>
                <form
                  id="share-project-overlay-form"
                  className="share-project-overlay__email-tab"
                  onSubmit={onSubmit}
                >
                  <TextInput.Root
                    name="email"
                    label={t("Project.ShareLinkReceiver")}
                    data-testid="share-project-overlay__email"
                  >
                    <TextInput.Label />
                    <TextInput.Focus>
                      <TextInput.Input
                        spellCheck={false}
                        data-testid="share-project-overlay__email__input"
                      />
                    </TextInput.Focus>
                    <TextInput.ErrorMessage />
                  </TextInput.Root>
                  <Textarea.Root
                    name="comment"
                    label={t("Project.ShareLinkComment")}
                  >
                    <Textarea.Label />
                    <Textarea.Focus>
                      <Textarea.Input
                        spellCheck={false}
                        data-testid="share-project-overlay__comment__input"
                      />
                    </Textarea.Focus>
                    <Textarea.ErrorMessage />
                  </Textarea.Root>
                  <Button variant={ButtonVariant.PRIMARY} type="submit">
                    {t("General.Share")}
                  </Button>
                </form>
              </FormProvider>
            </Tab>
          </Tabs>
        </div>
      </OverlayWrapper>
    )
  }
)
